import * as React from 'react';
import { FrontLayout } from 'client/ui/layout/FrontLayout';
import { Link, Redirect, RouteComponentProps } from 'react-router-dom';
import {
  Button,
  Card,
  Col,
  Divider,
  Form,
  Result,
  Row,
  Typography
} from 'antd';
const { Title, Text, Paragraph } = Typography;
import qs from 'qs';

import { useApiQuery } from 'client/core/network/hooks/useApiQuery';
import { LoadingPage } from 'client/ui/loader/LoadingPage';
import { AlertPage } from 'client/ui/alert/AlertPage';
import { getNetworkErrorMessage } from 'client/core/network/errors/getNetworkErrorMessage';
import { useBreadcrumbItem } from 'client/core/router/breadcrumb/BreadcrumbContext';
import { SecondaryText } from 'client/ui/display/text/SecondaryText';
import { RequestApi } from 'client/components/schema/requests/RequestApi';
import { Formik } from 'formik';
import { FormikForm } from 'client/ui/form/FormikForm';
import { TextInputItem } from 'client/ui/form/input/TextInputItem';
import { LockOutlined, UserOutlined } from '@ant-design/icons';
import { useThemeLabel } from 'client/components/schema/config/ClientConfigModule';
import { IUserCreateDto, IUserEditDto } from 'common/dto/UserDto';
import { PreventNavigation } from 'client/routes/navigation/PreventNavigation';
import { VerticalSpace } from 'client/ui/space/VerticalSpace';
import { CheckboxInputItem } from 'client/ui/form/input/CheckboxInputItem';
import { FormDividerSpace } from 'client/ui/space/FormDividerSpace';
import { DatePickerInputItem } from 'client/ui/form/input/DatePickerInputItem';
import { LinkButton } from 'client/ui/link/LinkButton';
import { logger } from 'client/core/logger/logger';
import { useApiMutation } from 'client/core/network/hooks/useApiMutation';
import { CitizenApi } from 'client/components/schema/citizen/CitizenApi';
import { schemaToValidator } from 'client/core/validation/schemaToValidator';
import { ICitizenActivateDto } from 'common/dto/CitizenDto';
import { useTranslation } from 'react-i18next';

interface Params {
  id: string;
}

export interface UserActivationPageProps extends RouteComponentProps<Params> {}

/**
 * Pagina di attivazione (conferma email) di un utente con email e password (senza SPID)
 */
export function UserActivationPage(props: UserActivationPageProps) {
  const { t } = useTranslation();

  const activationToken = qs.parse(props.location.search.substr(1)).token;
  logger.log(`Ricevuto token`, activationToken);

  useBreadcrumbItem({
    path: '/',
    title: t('procedures.breadcrumb', 'Istanze')
  });
  useBreadcrumbItem({
    path: props.location.pathname,
    title: t('user.activation.breadcrumb', 'Attivazione Utente')
  });

  const [activate, activateState] = useApiMutation(CitizenApi.activate, {});

  const layout = {
    labelCol: { span: 6 },
    wrapperCol: { span: 12 }
  };
  const tailLayout = {
    wrapperCol: { sm: { offset: 6, span: 12 }, xs: { offset: 0, span: 24 } }
  };
  return (
    <FrontLayout>
      <div style={{ paddingTop: '20px', paddingBottom: '50px' }}>
        <Card>
          {!activationToken || typeof activationToken !== 'string' ? (
            <Redirect to="/" />
          ) : activateState.response ? (
            <Result
              status="success"
              title={t(
                'user.activation.response.success.title',
                'Registrazione completata!'
              )}
              subTitle={t(
                'user.activation.response.success.sub-title',
                `Effettua l'accesso con email e password inserite nel modulo di registrazione.`
              )}
              extra={
                <LinkButton
                  type="primary"
                  to="/utente/login"
                  children={t(
                    'user.activation.response.success.submit-button',
                    'Vai al Login'
                  )}
                />
              }
            />
          ) : activateState.error ? (
            <Result
              status="error"
              title={t(
                'user.activation.response.error.title',
                `Errore nella verifica dell'email`
              )}
              subTitle={`${t(
                'user.activation.response.error.sub-title',
                `Non è stato possibile verificare l'email.`
              )} ${getNetworkErrorMessage(activateState.error)}`}
            />
          ) : (
            <Formik<ICitizenActivateDto>
              enableReinitialize
              initialValues={{
                activationToken
              }}
              validateOnChange={false}
              onSubmit={async rawValues => {
                try {
                  await activate({
                    data: {
                      input: rawValues
                    }
                  });
                } catch (e) {
                  logger.error(e);
                }
              }}
            >
              {({ isSubmitting, dirty }) => (
                <>
                  <PreventNavigation
                    when={dirty && !isSubmitting}
                    message={t(
                      'user.registration.prevent-navigation-message',
                      `Ci sono dati non salvati, abbandonare la pagina? (Tutte le modifiche non salvate andranno perse)`
                    )}
                  />
                  {/* Form Utente */}
                  <FormikForm {...layout}>
                    <Form.Item {...tailLayout}>
                      <Title level={2}>
                        {t('user.activation.title', `Verifica E-Mail`)}
                      </Title>
                      <Paragraph>
                        {t(
                          'user.activation.text',
                          `Premi sul pulsante di seguito per completare la verifica della tua E-Mail.`
                        )}
                      </Paragraph>
                    </Form.Item>

                    <Form.Item {...tailLayout}>
                      <Button
                        htmlType="submit"
                        type="primary"
                        style={{ width: '100%' }}
                      >
                        {t('user.activation.submit-button', `Attiva Utente`)}
                      </Button>
                    </Form.Item>
                  </FormikForm>
                </>
              )}
            </Formik>
          )}
        </Card>
      </div>
    </FrontLayout>
  );
}
